import type { Task } from '@portfolio-pm/shared';

export type TaskRowDateMode = 'due' | 'scheduled';

export function startOfDayLocal(value: number | Date): number {
  const date = typeof value === 'number' ? new Date(value) : new Date(value.getTime());
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

export function addDays(value: number, days: number): number {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date.getTime();
}

export function startOfMonth(value: number): number {
  const date = new Date(value);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

export function startOfMonthGrid(value: number): number {
  const date = new Date(startOfMonth(value));
  date.setDate(date.getDate() - date.getDay());
  return date.getTime();
}

export function isSameDayLocal(left: number, right: number): boolean {
  return startOfDayLocal(left) === startOfDayLocal(right);
}

export function toDayKey(value: number): string {
  const date = new Date(startOfDayLocal(value));
  const pad = (part: number): string => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Intl.DateTimeFormat construction is expensive; these run in per-task
// formatting paths, so each distinct formatter is built once at module scope.
const MONTH_YEAR_FORMAT = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
const MONTH_DAY_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});
const MONTH_DAY_FORMAT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const WEEKDAY_SHORT_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'short' });
const WEEKDAY_MONTH_DAY_FORMAT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});
const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

export function formatMonthLabel(value: number): string {
  return MONTH_YEAR_FORMAT.format(new Date(value));
}

export function formatShortDate(value: number): string {
  return MONTH_DAY_TIME_FORMAT.format(new Date(value));
}

export function formatTaskDate(value: number, hasTime: boolean | undefined): string {
  return (hasTime ? MONTH_DAY_TIME_FORMAT : MONTH_DAY_FORMAT).format(new Date(value));
}

export function formatTaskDueLabel(task: Task): string {
  if (task.dueAt !== undefined) return `Due ${formatTaskDate(task.dueAt, task.dueAtHasTime)}`;
  if (task.scheduledAt !== undefined) {
    return `Scheduled ${formatTaskDate(task.scheduledAt, task.scheduledAtHasTime)}`;
  }
  return 'No date';
}

export function getTaskRowDate(
  task: Task,
  mode: TaskRowDateMode,
  now: number = Date.now(),
): { label: string; value: number; hasTime: boolean | undefined } | undefined {
  if (mode === 'scheduled' && task.scheduledAt !== undefined) {
    return {
      label: `Scheduled ${formatTaskRowDate(task.scheduledAt, task.scheduledAtHasTime, now)}`,
      value: task.scheduledAt,
      hasTime: task.scheduledAtHasTime,
    };
  }

  if (task.dueAt !== undefined) {
    return {
      label: `Due ${formatTaskRowDate(task.dueAt, task.dueAtHasTime, now)}`,
      value: task.dueAt,
      hasTime: task.dueAtHasTime,
    };
  }

  if (task.scheduledAt !== undefined) {
    return {
      label: `Scheduled ${formatTaskRowDate(task.scheduledAt, task.scheduledAtHasTime, now)}`,
      value: task.scheduledAt,
      hasTime: task.scheduledAtHasTime,
    };
  }

  return undefined;
}

export function formatTaskRowDateLabel(
  task: Task,
  mode: TaskRowDateMode,
  now: number = Date.now(),
): string {
  return getTaskRowDate(task, mode, now)?.label ?? 'No date';
}

export function isTaskRowDateOverdue(
  task: Task,
  mode: TaskRowDateMode,
  now: number = Date.now(),
): boolean {
  if (task.completedAt !== undefined) return false;
  const rowDate = getTaskRowDate(task, mode, now);
  if (rowDate === undefined) return false;
  return endOfDateOnlyDay(rowDate.value, rowDate.hasTime) < now;
}

function formatTaskRowDate(value: number, hasTime: boolean | undefined, now: number): string {
  const date = new Date(value);
  const dayDiff = getDayDifference(value, now);
  if (dayDiff === -1) return appendTimeIfNeeded('Yesterday', value, hasTime);
  if (dayDiff === 0) return appendTimeIfNeeded('Today', value, hasTime);
  if (dayDiff === 1) return appendTimeIfNeeded('Tomorrow', value, hasTime);
  if (dayDiff >= 2 && dayDiff <= 6) {
    const weekday = WEEKDAY_SHORT_FORMAT.format(date);
    return appendTimeIfNeeded(weekday, value, hasTime);
  }

  if (date.getFullYear() === new Date(now).getFullYear()) {
    return formatTaskDate(value, hasTime);
  }

  const pad = (part: number): string => String(part).padStart(2, '0');
  const dateLabel = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${pad(
    date.getFullYear() % 100,
  )}`;
  if (!hasTime) return dateLabel;

  return `${dateLabel}, ${TIME_FORMAT.format(date)}`;
}

function appendTimeIfNeeded(label: string, value: number, hasTime: boolean | undefined): string {
  if (!hasTime) return label;
  return `${label}, ${formatTimeLabel(value)}`;
}

function getDayDifference(value: number, now: number): number {
  const startValue = startOfDayLocal(value);
  const startNow = startOfDayLocal(now);
  return Math.round((startValue - startNow) / (24 * 60 * 60 * 1000));
}

function endOfDateOnlyDay(value: number, hasTime: boolean | undefined): number {
  if (hasTime) return value;
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date.getTime();
}

export function formatDayLabel(value: number): string {
  return WEEKDAY_MONTH_DAY_FORMAT.format(new Date(value));
}

export function formatTimeLabel(value: number): string {
  return TIME_FORMAT.format(new Date(value));
}

export function moveDatePreservingTime(source: number, targetDay: number): number {
  const next = new Date(targetDay);
  const sourceDate = new Date(source);
  next.setHours(
    sourceDate.getHours(),
    sourceDate.getMinutes(),
    sourceDate.getSeconds(),
    sourceDate.getMilliseconds(),
  );
  return next.getTime();
}

export function defaultScheduledAt(targetDay: number): number {
  return startOfDayLocal(targetDay);
}
